/**
 * Évite que des points superposés (mêmes km/prix) se masquent les uns les autres
 * sur les scatter plots prix vs kilométrage.
 *
 * Stratégie en deux passes :
 *  1) Cluster strict (cellule (tolX, tolY) partagée) → étalement en grille
 *     compacte autour du centre. Cas typique : 5 motos neuves à km = 0 et au
 *     même prix de catalogue, l'une masque les 4 autres.
 *  2) Cluster vertical (mêmes km mais prix différents) → léger décalage
 *     horizontal **côte à côte**, sans toucher au prix. Cas typique : moto
 *     neuve à km=0 + concurrent neuf à km=0 mais prix différent → l'œil voit
 *     une seule colonne verticale → on écarte les points symétriquement
 *     autour du km réel pour qu'ils ne soient plus alignés.
 *
 * Les valeurs réelles sont préservées dans `kmReal` / `prixReal` pour le tooltip.
 *
 * Les rayons d'écartement sont **proportionnels au domaine affiché** (et non
 * pas des valeurs absolues), pour rester visibles sur un graphe étroit
 * (ex. domaine km = 0-200 quand tout le monde est à 0 km) sans déborder.
 *
 * @param {Array<object>} points  - liste d'objets contenant `km` et `prix`
 * @param {{ kmMin:number, kmMax:number, prixMin:number, prixMax:number }} domains
 * @param {object} [opts]
 * @param {number} [opts.toleranceXPct=0.03] - largeur de cellule (% du domaine X).
 *   Calibré pour matcher la taille pixel du marker (rayon 6px ≈ 12-15px de
 *   chevauchement visuel ≈ 2-3 % d'un domaine X typique 0-50 000 km).
 *   Une valeur trop petite (1 %) ratait des superpositions visuelles
 *   évidentes — typiquement 2 points à 200 km l'un de l'autre sur un graphe
 *   où 1 % vaut 120 km, vu comme un seul disque.
 * @param {number} [opts.toleranceYPct=0.025] - hauteur de cellule (% du domaine Y).
 * @param {number} [opts.spreadXPct=0.05] - demi-écartement horizontal (% du domaine X)
 * @param {number} [opts.spreadYPct=0.04] - demi-écartement vertical (% du domaine Y)
 * @param {number} [opts.verticalSpreadXPct=0.04] - décalage horizontal pour les
 *   points partageant le même km mais prix différents (% du domaine X)
 * @returns {Array<object>} nouveaux points (mêmes refs si pas de cluster)
 */
export function applyClusterJitter(points, domains, opts = {}) {
  if (!Array.isArray(points) || points.length === 0 || !domains) return points || []
  const {
    toleranceXPct = 0.03,
    toleranceYPct = 0.025,
    spreadXPct = 0.05,
    spreadYPct = 0.04,
    verticalSpreadXPct = 0.04,
  } = opts

  const xRange = Math.max(1, Number(domains.kmMax) - Number(domains.kmMin))
  const yRange = Math.max(1, Number(domains.prixMax) - Number(domains.prixMin))
  const tolX = Math.max(1, xRange * toleranceXPct)
  const tolY = Math.max(1, yRange * toleranceYPct)
  // Demi-écartement strictement borné en % du domaine, jamais en absolu.
  // Sur un domaine km 0-200 → spreadX ≈ 10 km, soit ~5 % du graphe = visible
  // sans dépasser le bord. Sur un domaine 0-100 000 km → spreadX = 5 000 km.
  const spreadX = Math.max(1, xRange * spreadXPct)
  const spreadY = Math.max(1, yRange * spreadYPct)
  const vertSpreadX = Math.max(1, xRange * verticalSpreadXPct)

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

  // Pré-allocate les sorties avec kmReal / prixReal préservés.
  const out = points.map((p) => ({
    ...p,
    kmReal: Number(p?.km),
    prixReal: Number(p?.prix),
  }))
  const handled = new Array(points.length).fill(false)

  // Passe 1 : cluster strict (mêmes km ET mêmes prix dans la cellule) →
  // étalement régulier sur une grille centrée autour du barycentre du
  // cluster pour rester homogène et toujours dans le domaine.
  const tightClusters = new Map()
  points.forEach((p, idx) => {
    const km = Number(p?.km)
    const prix = Number(p?.prix)
    if (!Number.isFinite(km) || !Number.isFinite(prix)) return
    const kx = Math.round(km / tolX)
    const ky = Math.round(prix / tolY)
    const key = `${kx}|${ky}`
    if (!tightClusters.has(key)) tightClusters.set(key, [])
    tightClusters.get(key).push(idx)
  })

  for (const group of tightClusters.values()) {
    const n = group.length
    if (n <= 1) continue
    // On dispose les points sur une grille rect carrée la plus compacte
    // possible (cols × rows ≥ n). Cela garantit un étalement visible et
    // déterministe, jamais clampé au bord du graphe.
    const cols = Math.ceil(Math.sqrt(n))
    const rows = Math.ceil(n / cols)
    // Pas entre deux colonnes/rangées : on étale entre −spread et +spread.
    const stepX = cols > 1 ? (2 * spreadX) / (cols - 1) : 0
    const stepY = rows > 1 ? (2 * spreadY) / (rows - 1) : 0
    // Coordonnées du centre = moyenne réelle du cluster, pour ne pas
    // « décaler » globalement le cluster lorsqu'il est près d'un bord.
    let sumKm = 0
    let sumPrix = 0
    for (const idx of group) {
      sumKm += out[idx].kmReal
      sumPrix += out[idx].prixReal
    }
    const centerKm = sumKm / n
    const centerPrix = sumPrix / n
    // On clampe le centre pour s'assurer que la grille rentre dans le domaine.
    const safeCenterKm = clamp(centerKm, domains.kmMin + spreadX, domains.kmMax - spreadX)
    const safeCenterPrix = clamp(centerPrix, domains.prixMin + spreadY, domains.prixMax - spreadY)
    group.forEach((idx, i) => {
      const col = i % cols
      const row = Math.floor(i / cols)
      const dx = cols > 1 ? -spreadX + col * stepX : 0
      const dy = rows > 1 ? -spreadY + row * stepY : 0
      const newKm = safeCenterKm + dx
      const newPrix = safeCenterPrix + dy
      out[idx].km = clamp(newKm, domains.kmMin, domains.kmMax)
      out[idx].prix = clamp(newPrix, domains.prixMin, domains.prixMax)
      handled[idx] = true
    })
  }

  // Passe 2 : cluster vertical (mêmes km, prix différents) sur les points
  // non encore traités. Décalage horizontal symétrique pour qu'ils ne soient
  // plus alignés sur la même verticale ; on n'altère pas le prix (axe Y).
  const verticalCols = new Map()
  points.forEach((p, idx) => {
    if (handled[idx]) return
    const km = Number(p?.km)
    const prix = Number(p?.prix)
    if (!Number.isFinite(km) || !Number.isFinite(prix)) return
    const kx = Math.round(km / tolX)
    if (!verticalCols.has(kx)) verticalCols.set(kx, [])
    verticalCols.get(kx).push(idx)
  })

  for (const group of verticalCols.values()) {
    const n = group.length
    if (n <= 1) continue
    // Tri par prix croissant pour un placement « vague » (esthétique).
    const sorted = [...group].sort((a, b) => out[a].prixReal - out[b].prixReal)
    sorted.forEach((idx, i) => {
      const t = n === 1 ? 0 : (i / (n - 1)) * 2 - 1 // ∈ [-1, 1]
      const offset = t * vertSpreadX
      const newKm = out[idx].kmReal + offset
      out[idx].km = clamp(newKm, domains.kmMin, domains.kmMax)
    })
  }

  return out
}
